/**
 * Community listing detail — mockup-aligned V2 layout.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { communityListingsService } from '../services/communityListingsService';
import NavigationService from '../../../services/navigationService';
import { YButton } from '../../../ui/YButton';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { MAX_PHOTOS } from '../constants/communityListings';
import Lightbox from '../../../components/common/Lightbox';
import { CommunityListingPageShell } from './CommunityListingPageShell';
import './community-listing-pages.css';

export function CommunityListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { currentUser } = useCurrentUser();
  const currentUserId = currentUser?.id;
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const fetchListing = () => communityListingsService.get(id).then(setListing);

  useEffect(() => {
    let cancelled = false;
    communityListingsService
      .get(id)
      .then((data) => {
        if (!cancelled) setListing(data);
      })
      .catch(() => {
        if (!cancelled) setError('Listing not found or unavailable.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleMessageSeller = () => {
    if (!listing?.author_id || !navigate) return;
    NavigationService.navigateToUserMessage(listing.author_id, 'Seller', navigate);
  };

  const handleEdit = () => {
    navigate(`/camp-essentials/gear/listings/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    try {
      await communityListingsService.delete(id);
      navigate('/camp-essentials/gear?tab=listings', { replace: true });
    } catch {
      setError('Could not delete listing.');
    }
  };

  const handleAddPhotos = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return;
    const currentCount = (listing.photos || []).length;
    const slotsLeft = MAX_PHOTOS - currentCount;
    if (slotsLeft <= 0) return;
    const toUpload = images.slice(0, slotsLeft);
    setUploadError('');
    setUploading(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      for (let i = 0; i < toUpload.length; i += 1) {
        await communityListingsService.uploadListingPhoto(id, toUpload[i], currentCount + i + 1);
      }
      await fetchListing();
    } catch (err) {
      setUploadError(err?.response?.data?.detail || err?.message || 'Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <CommunityListingPageShell title="Listing" subtitle="Marketplace" testId="community-listing-detail">
        <p className="community-listing-page__loading">Loading…</p>
      </CommunityListingPageShell>
    );
  }

  if (error || !listing) {
    return (
      <CommunityListingPageShell title="Listing" subtitle="Marketplace" testId="community-listing-detail">
        <p className="community-listing-page__error">{error || 'Listing not found.'}</p>
      </CommunityListingPageShell>
    );
  }

  const isAuthor = currentUserId != null && Number(listing.author_id) === Number(currentUserId);
  const daysLeft = listing.days_until_auto_removal;
  const priceStr =
    listing.price != null && listing.price !== ''
      ? `$${Number(listing.price).toLocaleString()}`
      : null;
  const photos = [...(listing.photos || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
  const heroPhoto = photos[0];

  return (
    <CommunityListingPageShell
      title={listing.title}
      subtitle="Marketplace"
      testId="community-listing-detail"
    >
      <article className="community-listing-detail-v2">
        {heroPhoto ? (
          <div className="community-listing-detail-v2__hero">
            <img src={heroPhoto.photo_url || heroPhoto.thumb_url} alt="" />
            <div className="community-listing-detail-v2__hero-overlay" aria-hidden="true" />
            {priceStr ? (
              <span className="community-listing-detail-v2__hero-price">{priceStr}</span>
            ) : null}
            {listing.condition_display ? (
              <span className="community-listing-detail-v2__hero-condition">
                {listing.condition_display}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className={heroPhoto ? '' : 'pt-0'}>
          <div className="community-listing-detail-v2__chips">
            {listing.category_display ? (
              <span className="community-listing-detail-v2__chip">{listing.category_display}</span>
            ) : null}
            {listing.status && listing.status !== 'active' ? (
              <span className="community-listing-detail-v2__chip">{listing.status}</span>
            ) : null}
            {!heroPhoto && priceStr ? (
              <span className="community-listing-detail-v2__chip">{priceStr}</span>
            ) : null}
          </div>

          {listing.location_text ? (
            <p className="community-listing-detail-v2__location">
              <FaMapMarkerAlt aria-hidden="true" /> {listing.location_text}
            </p>
          ) : null}

          {daysLeft != null && listing.status === 'active' ? (
            <div className="community-listing-detail-v2__countdown">
              {daysLeft === 0
                ? 'This listing is removed today.'
                : daysLeft <= 10
                  ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left — automatically removed after 60 days.`
                  : `Removed in ${daysLeft} days (auto-removed after 60 days).`}
            </div>
          ) : null}

          {photos.length > 1 ? (
            <div className="community-listing-detail-v2__gallery" aria-label="Listing photos">
              {photos.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  className="community-listing-detail-v2__gallery-thumb"
                  onClick={() => {
                    setLightboxIndex(idx);
                    setShowLightbox(true);
                  }}
                  aria-label={`View photo ${idx + 1} of ${photos.length}`}
                >
                  <img src={p.photo_url || p.thumb_url} alt="" />
                </button>
              ))}
            </div>
          ) : null}

          {isAuthor && photos.length < MAX_PHOTOS ? (
            <div className="mb-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="d-none"
                onChange={handleAddPhotos}
                disabled={uploading}
                aria-label="Add more photos"
              />
              <YButton
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? 'Uploading…' : `Add photos (${photos.length}/${MAX_PHOTOS})`}
              </YButton>
              {uploadError ? <p className="community-listing-page__error mt-2 mb-0">{uploadError}</p> : null}
            </div>
          ) : null}

          {listing.description ? (
            <div className="community-listing-detail-v2__description">{listing.description}</div>
          ) : null}

          {showLightbox && photos.length > 0 ? (
            <Lightbox
              items={photos.map((p) => ({
                caption: '',
                url: p.photo_url || p.thumb_url || '',
                getUrl: () => p.photo_url || p.thumb_url || '',
              }))}
              initialIndex={lightboxIndex}
              onClose={() => setShowLightbox(false)}
            />
          ) : null}

          <div className="community-listing-detail-v2__actions">
            {!isAuthor && listing.author_id ? (
              <YButton variant="primary" onClick={handleMessageSeller}>
                Message seller
              </YButton>
            ) : null}
            {isAuthor ? (
              <>
                <YButton variant="outline-secondary" onClick={handleEdit}>
                  Edit listing
                </YButton>
                <YButton variant="outline-danger" onClick={handleDelete}>
                  Delete listing
                </YButton>
              </>
            ) : null}
          </div>
        </div>
      </article>
    </CommunityListingPageShell>
  );
}
